/**
 * AI Transfer Actions Service
 * Provides AI-compatible actions for transfer type changes in Hospital, Community, Deceased, and Admission pages
 * Supports changing between planned/unplanned transfers (Hospital), Safe Discharge/SNF/AMA (Community), All (Deceased), and Admission/Readmission (Admission)
 */

import { TYPES } from '../data/hospital.data';
import { TRANSFER_TYPE } from '../types/community-transfer.type';
import { DECEASED_TYPES } from '../data/common.data';
import { ADMISSION_TYPES } from '../data/admission.data';

class AiTransferActionsService {
    constructor() {
        this.handleOnclickCard = null;
        this.handleClickTotal = null;
        this.getState = null;
        this.pageType = null; // 'hospital', 'communityTransfer', or 'deceased'
    }

    /**
     * Initialize the service with required functions
     * @param {Function} handleOnclickCard - Transfer type selection function
     * @param {Function} handleClickTotal - Total transfer selection function
     * @param {Function} getState - Function to get current state
     */
    initialize(handleOnclickCard, handleClickTotal, getState) {
        this.handleOnclickCard = handleOnclickCard;
        this.handleClickTotal = handleClickTotal;
        this.getState = getState;
        
        // Detect page type from state structure
        const state = getState();
        if (state.hospital) {
            this.pageType = 'hospital';
        } else if (state.communityTransfer) {
            this.pageType = 'communityTransfer';
        } else if (state.deceased) {
            this.pageType = 'deceased';
        } else if (state.admission) {
            this.pageType = 'admission';
        }
        
        console.log('🔧 AI Transfer Actions Service initialized for:', this.pageType);
    }

    /**
     * Get available transfer types
     * @returns {Object} Available transfer types
     */
    getAvailableTransferTypes() {
        if (!this.getState) return {};

        const state = this.getState();
        const pageState = state[this.pageType] || state.hospital || state.communityTransfer || state.deceased || state.admission || {};
        const { dbData, transferType } = pageState;

        // If community transfer page, return community-specific transfer types
        if (this.pageType === 'communityTransfer') {
            return this.getCommunityTransferTypes(dbData, transferType);
        }

        // If deceased page, return deceased-specific transfer types
        if (this.pageType === 'deceased') {
            return this.getDeceasedTransferTypes(dbData, transferType);
        }

        // If admission page, return admission-specific transfer types
        if (this.pageType === 'admission') {
            return this.getAdmissionTransferTypes(dbData, transferType);
        }

        // Hospital page transfer types
        return {
            all: {
                type: TYPES.ALL,
                label: 'All Transfers',
                description: 'Show all hospital transfers (planned + unplanned)',
                count: dbData?.total || 0,
                isSelected: transferType === TYPES.ALL,
                aliases: ['all', 'total', 'all transfers', 'total transfers']
            },
            planned: {
                type: TYPES.PLANNED,
                label: 'Planned Transfers',
                description: 'Show only planned hospital transfers',
                count: dbData?.plannedHospitalTransfer || 0,
                isSelected: transferType === TYPES.PLANNED,
                aliases: ['planned', 'planned transfers', 'scheduled', 'scheduled transfers']
            },
            unplanned: {
                type: TYPES.UNPLANNED,
                label: 'Unplanned Transfers',
                description: 'Show only unplanned hospital transfers',
                count: dbData?.unplannedHospitalTransfer || 0,
                isSelected: transferType === TYPES.UNPLANNED,
                aliases: ['unplanned', 'unplanned transfers', 'emergency', 'urgent', 'unscheduled']
            },
            none: {
                type: null,
                label: 'No Filter',
                description: 'Clear transfer type filter',
                count: dbData?.total || 0,
                isSelected: !transferType,
                aliases: ['none', 'clear', 'reset', 'no filter', 'remove filter']
            }
        };
    }

    /**
     * Get Community Transfer page transfer types
     * @param {Object} dbData - Database data
     * @param {Array} transferType - Current transfer types (array for community)
     * @returns {Object} Available transfer types
     */
    getCommunityTransferTypes(dbData, transferType) {
        const isArray = Array.isArray(transferType);
        return {
            all: {
                type: TRANSFER_TYPE.ALL,
                label: 'All Community Transfers',
                description: 'Show all community transfers',
                count: dbData?.total || 0,
                isSelected: isArray ? transferType.includes(TRANSFER_TYPE.ALL) : transferType === TRANSFER_TYPE.ALL,
                aliases: ['all', 'total', 'all transfers', 'total transfers']
            },
            safeDischarge: {
                type: TRANSFER_TYPE.SAFE_DISCHARGE,
                label: 'Safe Discharge',
                description: 'Show safe discharge transfers',
                count: dbData?.totalSafeDischarge || 0,
                isSelected: isArray ? transferType.includes(TRANSFER_TYPE.SAFE_DISCHARGE) : transferType === TRANSFER_TYPE.SAFE_DISCHARGE,
                aliases: ['safe discharge', 'safe', 'discharge', 'home']
            },
            snf: {
                type: TRANSFER_TYPE.SNF,
                label: 'SNF Transfers',
                description: 'Show SNF transfers',
                count: dbData?.totalSNF || 0,
                isSelected: isArray ? transferType.includes(TRANSFER_TYPE.SNF) : transferType === TRANSFER_TYPE.SNF,
                aliases: ['snf', 'snf transfers', 'skilled nursing', 'skilled nursing facility']
            },
            ama: {
                type: TRANSFER_TYPE.AMA,
                label: 'AMA',
                description: 'Show AMA transfers',
                count: dbData?.totalAMA || 0,
                isSelected: isArray ? transferType.includes(TRANSFER_TYPE.AMA) : transferType === TRANSFER_TYPE.AMA,
                aliases: ['ama', 'against medical advice']
            },
            none: {
                type: null,
                label: 'No Filter',
                description: 'Clear transfer type filter',
                count: dbData?.total || 0,
                isSelected: !transferType || (isArray && transferType.length === 0),
                aliases: ['none', 'clear', 'reset', 'no filter', 'remove filter']
            }
        };
    }

    /**
     * Get Deceased page transfer types
     * Note: Deceased page only has "ALL" - no sub-categories like planned/unplanned
     * @param {Object} dbData - Database data
     * @param {string} transferType - Current transfer type
     * @returns {Object} Available transfer types
     */
    getDeceasedTransferTypes(dbData, transferType) {
        return {
            all: {
                type: DECEASED_TYPES.ALL,
                label: 'All Deceased',
                description: 'Show all deceased residents',
                count: dbData?.total || 0,
                isSelected: transferType === DECEASED_TYPES.ALL,
                aliases: ['all', 'total', 'all deceased', 'total deceased', 'show all']
            },
            none: {
                type: null,
                label: 'No Filter',
                description: 'Clear deceased filter',
                count: dbData?.total || 0,
                isSelected: !transferType,
                aliases: ['none', 'clear', 'reset', 'no filter', 'remove filter']
            }
        };
    }

    /**
     * Get Admission page transfer types
     * @param {Object} dbData - Database data
     * @param {string} transferType - Current transfer type
     * @returns {Object} Available transfer types
     */
    getAdmissionTransferTypes(dbData, transferType) {
        return {
            all: {
                type: ADMISSION_TYPES.ALL,
                label: 'All Admissions',
                description: 'Show all admissions (new admissions + readmissions)',
                count: dbData?.total || 0,
                isSelected: transferType === ADMISSION_TYPES.ALL,
                aliases: ['all', 'total', 'all admissions', 'total admissions', 'both']
            },
            admission: {
                type: ADMISSION_TYPES.ADMISSION,
                label: 'New Admissions',
                description: 'Show only new admissions (first-time patients)',
                count: dbData?.admission || 0,
                isSelected: transferType === ADMISSION_TYPES.ADMISSION,
                aliases: ['admission', 'admissions', 'new admission', 'new admissions', 'new patients', 'first admission']
            },
            readmission: {
                type: ADMISSION_TYPES.READMISSION,
                label: 'Readmissions',
                description: 'Show only readmissions (returning patients)',
                count: dbData?.readmission || 0,
                isSelected: transferType === ADMISSION_TYPES.READMISSION,
                aliases: ['readmission', 'readmissions', 'returning', 'returning patients', 'return', 're-admission', 're-admissions']
            },
            none: {
                type: null,
                label: 'No Filter',
                description: 'Clear admission type filter',
                count: dbData?.total || 0,
                isSelected: !transferType,
                aliases: ['none', 'clear', 'reset', 'no filter', 'remove filter']
            }
        };
    }

    /**
     * Find transfer type by name or alias
     * @param {string} transferName - Transfer type name or alias
     * @returns {Object|null} Transfer type info
     */
    findTransferTypeByName(transferName) {
        const transferTypes = this.getAvailableTransferTypes();
        const lowerName = transferName.toLowerCase().trim();

        // First try exact match on keys
        if (transferTypes[lowerName]) {
            return { key: lowerName, ...transferTypes[lowerName] };
        }

        // Then try aliases
        for (const [key, transferType] of Object.entries(transferTypes)) {
            if (transferType.aliases.some(alias => 
                lowerName.includes(alias) || alias.includes(lowerName)
            )) {
                return { key, ...transferType };
            }
        }

        return null;
    }

    /**
     * Change transfer type
     * @param {string} transferTypeName - Transfer type name
     * @returns {Promise} Transfer type change result
     */
    async changeTransferType(transferTypeName) {
        if (!this.handleOnclickCard && !this.handleClickTotal) {
            throw new Error('Service not initialized. Call initialize() first.');
        }

        const transferTypeInfo = this.findTransferTypeByName(transferTypeName);
        
        if (!transferTypeInfo) {
            throw new Error(`Transfer type '${transferTypeName}' not found`);
        }

        const state = this.getState();
        const pageState = state[this.pageType] || state.hospital || state.communityTransfer || {};
        const { dbData } = pageState;

        try {
            if (transferTypeInfo.type === TYPES.ALL) {
                // Use handleClickTotal for "all" transfers
                await this.handleClickTotal(dbData?.total || 0);
            } else if (transferTypeInfo.type === TYPES.PLANNED) {
                // Use handleOnclickCard for planned transfers
                await this.handleOnclickCard(TYPES.PLANNED, dbData?.plannedHospitalTransfer || 0, dbData?.total || 0);
            } else if (transferTypeInfo.type === TYPES.UNPLANNED) {
                // Use handleOnclickCard for unplanned transfers
                await this.handleOnclickCard(TYPES.UNPLANNED, dbData?.unplannedHospitalTransfer || 0, dbData?.total || 0);
            } else {
                // Clear transfer type (set to null)
                await this.handleOnclickCard(null, 0, dbData?.total || 0);
            }

            return {
                success: true,
                message: `Transfer type changed to ${transferTypeInfo.label}`,
                transferType: transferTypeInfo.type,
                label: transferTypeInfo.label,
                count: transferTypeInfo.count
            };
        } catch (error) {
            console.error('Error changing transfer type:', error);
            throw new Error(`Failed to change transfer type: ${error.message}`);
        }
    }

    /**
     * Get current transfer type status
     * @returns {Object} Current transfer type status
     */
    getCurrentTransferStatus() {
        const transferTypes = this.getAvailableTransferTypes();
        const currentType = Object.values(transferTypes).find(type => type.isSelected);

        // Base status object
        const status = {
            currentType: currentType?.type || null,
            currentLabel: currentType?.label || 'No Filter',
            currentCount: currentType?.count || 0,
            availableTypes: Object.keys(transferTypes),
            totalAll: transferTypes.all?.count || 0
        };

        // Add page-specific transfer counts and percentages
        if (this.pageType === 'admission') {
            // Admission page: admission vs readmission
            status.totalAdmissions = transferTypes.admission?.count || 0;
            status.totalReadmissions = transferTypes.readmission?.count || 0;
            status.percentages = {
                admissions: transferTypes.all?.count > 0 ? 
                    ((transferTypes.admission?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0,
                readmissions: transferTypes.all?.count > 0 ? 
                    ((transferTypes.readmission?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0
            };
        } else if (this.pageType === 'hospital') {
            // Hospital page: planned vs unplanned
            status.totalPlanned = transferTypes.planned?.count || 0;
            status.totalUnplanned = transferTypes.unplanned?.count || 0;
            status.percentages = {
                planned: transferTypes.all?.count > 0 ? 
                    ((transferTypes.planned?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0,
                unplanned: transferTypes.all?.count > 0 ? 
                    ((transferTypes.unplanned?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0
            };
        } else if (this.pageType === 'communityTransfer') {
            // Community Transfer page: Safe Discharge, SNF, AMA
            status.totalSafeDischarge = transferTypes.safeDischarge?.count || 0;
            status.totalSNF = transferTypes.SNF?.count || 0;
            status.totalAMA = transferTypes.AMA?.count || 0;
            status.percentages = {
                safeDischarge: transferTypes.all?.count > 0 ? 
                    ((transferTypes.safeDischarge?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0,
                SNF: transferTypes.all?.count > 0 ? 
                    ((transferTypes.SNF?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0,
                AMA: transferTypes.all?.count > 0 ? 
                    ((transferTypes.AMA?.count || 0) / transferTypes.all.count * 100).toFixed(1) : 0
            };
        }

        return status;
    }

    /**
     * Get AI action suggestions for transfer types
     * @returns {Array} AI action suggestions
     */
    getAIActionSuggestions() {
        const transferTypes = this.getAvailableTransferTypes();
        const suggestions = [];

        Object.entries(transferTypes).forEach(([key, transferType]) => {
            if (!transferType.isSelected) {
                suggestions.push({
                    action: 'changeTransferType',
                    description: `Switch to ${transferType.label}`,
                    parameters: { transferTypeName: key },
                    example: `Show ${transferType.label.toLowerCase()}`,
                    count: transferType.count
                });
            }
        });

        return suggestions;
    }

    /**
     * Handle transfer type change from natural language input
     * @param {string} input - Natural language input
     * @returns {Promise} Transfer type change result
     */
    async handleTransferTypeFromInput(input) {
        const lowerInput = input.toLowerCase();
        
        // Try to find transfer type in the input
        const transferTypes = this.getAvailableTransferTypes();
        
        for (const [key, transferType] of Object.entries(transferTypes)) {
            if (transferType.aliases.some(alias => lowerInput.includes(alias))) {
                return await this.changeTransferType(key);
            }
        }

        throw new Error(`Could not determine transfer type from input: "${input}"`);
    }
}

// Export singleton instance
const aiTransferActionsService = new AiTransferActionsService();
export default aiTransferActionsService;
